import { useAuth } from '../lib/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Target, Hash, MessageSquare, Settings, LogOut, Plus, Menu } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CodyIcon } from './CodyLogo';
import { useState } from 'react';
import { motion } from 'framer-motion';

const navItems = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Leads', icon: Target, path: '/leads' },
  { label: 'Keywords', icon: Hash, path: '/keywords' },
  { label: 'Subreddits', icon: MessageSquare, path: '/subreddits' },
  { label: 'Settings', icon: Settings, path: '/settings' },
];

function SidebarContent({ onNavigate }) {
  const { user, logout, activeBusiness, businesses, switchBusiness } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const allNavItems = navItems;

  const handleNav = (path) => {
    navigate(path);
    if (onNavigate) onNavigate();
  };

  const isActive = (item) => {
    const p = location.pathname;
    if (item.path === '/dashboard') return p === '/dashboard';
    if (item.path === '/leads') return p === '/leads' || p.startsWith('/leads/');
    return p === item.path;
  };

  return (
    <div className="flex flex-col h-full bg-white" data-testid="app-sidebar-nav">
      {/* Logo */}
      <div className="p-5 border-b border-[#E6E8EE]">
        <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => handleNav('/dashboard')}>
          <CodyIcon size={36} />
          <span className="font-bold text-[15px] tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Cody</span>
        </div>
      </div>

      {/* Business Switcher */}
      {businesses.length > 0 && (
        <div className="px-4 py-3 border-b border-[#E6E8EE]">
          <Select
            value={activeBusiness?._id || ''}
            onValueChange={(val) => {
              const biz = businesses.find(b => b._id === val);
              if (biz) switchBusiness(biz);
            }}
          >
            <SelectTrigger className="w-full h-9 text-sm bg-[#F6F7F9] border-[#E6E8EE] rounded-lg" data-testid="business-switcher">
              <SelectValue placeholder="Select business" />
            </SelectTrigger>
            <SelectContent>
              {businesses.map(b => (
                <SelectItem key={b._id} value={b._id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-2 text-xs text-[#5B667A] hover:text-[#FF4500] hover:bg-[#FFF2EA]"
            onClick={() => handleNav('/onboard')}
            data-testid="add-business-button"
          >
            <Plus className="w-3 h-3 mr-1" /> Add Business
          </Button>
        </div>
      )}

      {/* Nav Items */}
      <nav className="flex-1 py-4 px-3">
        <p className="text-[10px] font-semibold text-[#9ca3af] uppercase tracking-widest px-3 mb-2">Navigation</p>
        {allNavItems.map((item) => {
          const active = isActive(item);
          return (
            <button
              key={item.label}
              onClick={() => handleNav(item.path)}
              className={`sidebar-item w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium mb-0.5 ${
                active
                  ? 'bg-gradient-to-r from-[#FFF2EA] to-[#FFFBF0] text-[#FF4500] shadow-sm'
                  : 'text-[#5B667A] hover:bg-[#F6F7F9] hover:text-[#0B0F17]'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="w-[18px] h-[18px]" />
              {item.label}
              {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#FF4500]" />}
            </button>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="p-4 border-t border-[#E6E8EE] bg-gradient-to-t from-[#FAFAFA] to-white">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#F6F7F9] to-[#E6E8EE] flex items-center justify-center text-xs font-bold text-[#5B667A]">
            {user?.email?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-[#0B0F17] truncate">{user?.email}</p>
            <p className="text-[10px] text-[#9ca3af] capitalize">{user?.role}</p>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-[#9ca3af] hover:text-[#FF4500]" onClick={logout} data-testid="logout-button">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function DashboardLayout({ children, title }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="dashboard-layout bg-[#FAFAFA] min-h-screen">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-[280px] bg-white border-r border-[#E6E8EE] fixed top-0 left-0 h-full z-30 shadow-[1px_0_3px_rgba(16,24,40,0.03)]">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white/80 backdrop-blur-xl border-b border-[#E6E8EE] flex items-center px-4 z-40">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="mobile-nav-open-button">
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[280px]">
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <span className="ml-3 font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Cody</span>
      </div>

      {/* Main Content */}
      <main className="dashboard-content lg:ml-[280px] pt-14 lg:pt-0 min-w-0 overflow-x-hidden">
        {title && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="border-b border-[#E6E8EE] bg-white px-6 py-5"
          >
            <h1 className="text-xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{title}</h1>
          </motion.div>
        )}
        <div className="p-4 sm:p-6 max-w-full overflow-hidden">
          {children}
        </div>
      </main>
    </div>
  );
}
